import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { sizesOf, splitPages, type PageSize } from '../editor/pageFormat';
import { fontNeeds, loadFonts } from '../fonts/load';
import { type BuiltinTemplate, BUILTIN_TEMPLATES, TPL_CATS, tplOwnWords, tplPhotoSlots, tplWords } from '../templates/builtin';
import { type AiImageInfo, api, ApiError, type DesignMeta, type TemplateCategory, type TemplateInfo, type TplAiState } from '../sync/api';
import type { Saver } from '../sync/saver';
import { fmtTime } from '../video/project';
import { $, $in, setPressed } from './dom';
import { closeSheets, openSheet } from './sheets';
import { toast } from './toast';

/*
 * "เทมเพลต" tab.
 *   งานออกแบบ  the ready-made templates (src/templates/builtin.ts, "สำเร็จรูป") and the group's templates, together,
 *              with a search box (choices 26 ต.ค. 2569) and chips for their categories. Tapping one shows it with
 *              three choices (choice ก "ถามทุกครั้ง"):
 *                แทนหน้านี้          the open page becomes the template (its size too, choice ก) — เลิกทำได้
 *                เพิ่มเป็นหน้าใหม่     the template's pages come after the open page
 *                เปิดเป็นงานใหม่      a new work of mine from it (the template itself never changes)
 *   วิดีโอ      video templates (0008): the preview video, then "ใช้เทมเพลตนี้" makes a new video work and `onUsed`
 *              opens its empty slots ("ช่องคลิป").
 * Anything that cannot be done says why (the open work can't be left, someone else's work, a page still loading…).
 *
 * รูปประกอบด้วย AI (0022, choice ก): a ready-made template whose slots an admin gave AI pictures gets them in its slots
 * when used (the pictures become the person's own, /take), and its card shows the preview drawn with them (when that
 * preview matches the pictures now in the slots; else the built-in one). Admins see "รูปประกอบด้วย AI" in its details:
 * every empty slot at once (the price first), or one slot again, or take one away — live for everyone at once.
 */
export interface TemplatesHooks {
  busy: () => string | null;
  onUsed: (d: DesignMeta) => void;
  /** a Thai reason when the open page cannot be changed now (someone else's work, a page still loading), else null */
  cannotEdit: () => string | null;
  isAdmin: () => boolean;
}

/** `own`: its own words (name, words, texts) · `hay`: those and its category's words */
type Item =
  | { src: 'builtin'; id: string; name: string; cat: string; own: string; hay: string; t: BuiltinTemplate }
  | { src: 'group'; id: string; name: string; cat: string; own: string; hay: string; t: TemplateInfo };

const builtinThumb = (t: BuiltinTemplate) => `/tpl/${t.id}.webp`;
const usd = (n: number) => n < 1 ? n.toFixed(3) : n.toFixed(2);

export function initTemplatesSheet(ed: Editor, history: History, saver: Saver, hooks: TemplatesHooks) {
  const chips = $('tplChips'), grid = $('tplGrid'), msg = $('tplMsg'), search = $in('tplSearch');
  const detail = $('tplDetail'), vid = $<HTMLVideoElement>('tplVideo'), poster = $<HTMLImageElement>('tplPoster');
  let cat = '';                       // '' = all · 'b:food' a ready-made category · 'g:<id>' a group category
  let kind: 'design' | 'video' = 'design';
  let cats: TemplateCategory[] = [];
  let list: TemplateInfo[] = [];
  let loadError = '';
  let busy = false;
  let chosen: Item | null = null;     // the template shown with its choices / preview
  // รูป AI (0022): the ready-made templates' AI pictures, and (admins) the service's prices and this month's use
  let ai: Record<string, TplAiState> = {};
  let aiError = '';
  let aiInfo: AiImageInfo | null = null;
  let aiBusy: { tpl: string; slot: number; started: number } | null = null;
  let aiStop = false;
  let aiTimer = 0;
  /** a ready-made template's preview: the one drawn with its AI pictures while it matches them, else the built-in one */
  const thumbOf = (t: BuiltinTemplate) => ai[t.id]?.thumb ?? builtinThumb(t);

  const say = (text: string) => { msg.textContent = text; msg.hidden = !text; };
  const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
  const kindOf = (t: TemplateInfo) => t.kind ?? 'design';
  const sizeOf = (it: Item) => {
    if (it.src === 'builtin') return `${it.t.w}×${it.t.h} px · สำเร็จรูป`;
    const t = it.t;
    return kindOf(t) === 'video'
      ? `วิดีโอ ${t.width === t.height ? '1:1' : '9:16'} · ${t.slots ?? '?'} ช่อง · ${fmtTime((t.durationMs ?? 0) / 1000)}`
      : `${t.width}×${t.height} px${(t.pages ?? 1) > 1 ? ` · ${t.pages} หน้า` : ''} · ของกลุ่ม`;
  };

  /** every template of the chosen kind, ready-made first in the design list */
  function items(): Item[] {
    const group: Item[] = list.filter(t => kindOf(t) === kind).map(t => {
      const c = cats.find(x => x.id === t.categoryId)?.name ?? '';
      return { src: 'group', id: t.id, name: t.name, cat: 'g:' + t.categoryId, own: t.name.toLowerCase(), hay: `${t.name} ${c} ของกลุ่ม`.toLowerCase(), t };
    });
    if (kind === 'video') return group;
    const built: Item[] = BUILTIN_TEMPLATES.map(t => ({ src: 'builtin', id: t.id, name: t.th, cat: 'b:' + t.cat, own: tplOwnWords(t), hay: tplWords(t) + ' สำเร็จรูป', t }));
    return [...group, ...built];
  }
  /**
   * The search: every word typed is found in the template or its category; the ones found in the template's own words
   * come first ("สงกรานต์" → สงกรานต์ first, then the other festival templates). Thai has no spaces between words, so
   * the browser splits the typed text into words (Intl.Segmenter; without it, at the spaces).
   */
  function searchWords(q: string): string[] {
    const t = q.toLowerCase().trim();
    if (!t) return [];
    const Seg = (Intl as unknown as { Segmenter?: new (l: string, o: { granularity: string }) => { segment: (s: string) => Iterable<{ segment: string; isWordLike?: boolean }> } }).Segmenter;
    if (!Seg) return t.split(/\s+/).filter(Boolean);
    return [...new Seg('th', { granularity: 'word' }).segment(t)].filter(x => x.isWordLike !== false && x.segment.trim()).map(x => x.segment.trim());
  }
  function found(all: Item[], q: string): Item[] {
    const words = searchWords(q);
    if (!words.length) return all;
    const whole = q.toLowerCase().trim();
    const score = (it: Item) => (it.own.includes(whole) ? 100 : 0) + words.filter(w => it.own.includes(w)).length;
    return all.filter(it => words.every(w => it.hay.includes(w)))
      .map((it, k) => ({ it, s: score(it), k })).sort((a, b) => b.s - a.s || a.k - b.k).map(x => x.it);
  }

  function chip(id: string, text: string) {
    const b = document.createElement('button');
    b.className = 'fchip'; b.textContent = text;
    b.setAttribute('aria-pressed', String(cat === id));
    b.onclick = () => { cat = id; paint(); };
    chips.append(b);
  }

  function paint() {
    setPressed('tplKind', b => b.dataset.v === kind);
    const all = items();
    const q = search.value.trim();
    const hits = found(all, q);
    chips.textContent = '';
    chip('', `ทั้งหมด ${hits.length}`);
    for (const c of cats) {
      const n = hits.filter(it => it.cat === 'g:' + c.id).length;
      if (n) chip('g:' + c.id, `${c.name} ${n}`);
    }
    if (kind === 'design') for (const c of TPL_CATS) {
      const n = hits.filter(it => it.cat === 'b:' + c.id).length;
      if (n) chip('b:' + c.id, `${c.th} ${n}`);
    }
    grid.textContent = '';
    grid.hidden = !!chosen;
    chips.hidden = !!chosen;   // one template's preview takes the place of the list
    search.hidden = !!chosen;
    const shown = hits.filter(it => !cat || it.cat === cat);
    if (!shown.length) {
      grid.append(Object.assign(document.createElement('p'), {
        className: 'empty',
        textContent: q ? `ไม่พบเทมเพลตที่ตรงกับ "${q}" ลองคำอื่น เช่น กาแฟ ลดราคา ทุเรียน สงกรานต์`
          : all.length ? 'ไม่มีเทมเพลตในหมวดนี้'
            : kind === 'video' ? 'ยังไม่มีเทมเพลตวิดีโอ ผู้ดูแลเพิ่มได้จากหน้าวิดีโอ → "ช่องคลิป" → บันทึกเป็นเทมเพลต'
              : 'ยังไม่มีเทมเพลต'
      }));
      return;
    }
    for (const it of shown) {
      const card = document.createElement('button');
      card.className = 'tcard';
      card.disabled = busy;
      const img = document.createElement('img');
      img.src = it.src === 'builtin' ? thumbOf(it.t) : it.t.thumb; img.alt = ''; img.loading = 'lazy';
      img.onerror = () => {
        // a preview with AI pictures that cannot be opened: the built-in one
        if (it.src === 'builtin' && !img.src.endsWith(builtinThumb(it.t))) { img.src = builtinThumb(it.t); return; }
        img.replaceWith(Object.assign(document.createElement('span'), { className: 'tnoimg', textContent: 'เปิดรูปตัวอย่างไม่ได้' }));
      };
      card.append(img, Object.assign(document.createElement('b'), { textContent: it.name }),
        Object.assign(document.createElement('small'), { textContent: sizeOf(it) + (it.src === 'group' && it.t.preview ? ' · มีวิดีโอตัวอย่าง' : '') }));
      card.setAttribute('aria-label', `ดูเทมเพลต ${it.name}`);
      card.onclick = () => showDetail(it);
      grid.append(card);
    }
  }

  const isVideo = (it: Item) => it.src === 'group' && kindOf(it.t) === 'video';

  function showDetail(it: Item) {
    chosen = it;
    say('');
    detail.hidden = false;
    $('tplDName').textContent = it.name;
    const video = isVideo(it), t = it.src === 'group' ? it.t : null;
    $('tplDInfo').textContent = video && t
      ? `${sizeOf(it)} · ใช้ไป ${t.uses} ครั้ง · ต้องใช้คลิปหรือรูป ${t.slots ?? '?'} ไฟล์ (เลือกทีเดียวหลายไฟล์ได้)` + (t.preview ? '' : ' · เทมเพลตนี้ยังไม่มีวิดีโอตัวอย่าง')
      : `${sizeOf(it)}${t ? ` · ใช้ไป ${t.uses} ครั้ง` : ''} · "แทนหน้านี้" เปลี่ยนขนาดหน้าเป็นขนาดของเทมเพลต และเลิกทำได้`;
    const preview = t?.preview;
    vid.hidden = !preview;
    poster.hidden = !!preview;
    if (preview) vid.src = preview; else { vid.removeAttribute('src'); poster.src = it.src === 'builtin' ? thumbOf(it.t) : it.t.thumb; }
    $('tplReplace').hidden = video; $('tplAdd').hidden = video;
    $('tplUse').textContent = video ? 'ใช้เทมเพลตนี้' : 'เปิดเป็นงานใหม่';
    paintButtons();
    paint();
    sayAi('');
    paintAi();
    if (aiBusy && it.src === 'builtin' && aiBusy.tpl === it.t.id) aiStop = false;   // opened again while it runs: keep going
    if (it.src === 'builtin' && hooks.isAdmin()) void openAi(it.t);
  }
  function paintButtons() {
    for (const id of ['tplUse', 'tplReplace', 'tplAdd']) ($(id) as HTMLButtonElement).disabled = busy;
  }
  function hideDetail() {
    if (aiBusy) aiStop = true;   // "สร้างทุกช่อง" stops after the slot it is on (that one is already paid for)
    chosen = null; detail.hidden = true;
    vid.pause(); vid.removeAttribute('src'); vid.load();
    paint();
  }
  // the panel closed (✕, backdrop, another panel): stop the preview's sound
  new MutationObserver(() => { if ($('shTemplates').hidden && chosen) hideDetail(); })
    .observe($('shTemplates'), { attributes: true, attributeFilter: ['hidden'] });
  vid.addEventListener('error', () => { if (chosen && isVideo(chosen) && chosen.src === 'group' && chosen.t.preview) say('เล่นวิดีโอตัวอย่างไม่ได้ (ไฟล์อาจหายจากที่เก็บ) ยังใช้เทมเพลตได้'); });
  $('tplBack').onclick = hideDetail;
  $('tplUse').onclick = () => { if (chosen) void openAsNew(chosen); };
  $('tplReplace').onclick = () => { if (chosen) void putIn(chosen, 'replace'); };
  $('tplAdd').onclick = () => { if (chosen) void putIn(chosen, 'add'); };
  $('tplKind').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    kind = b.dataset.v as 'design' | 'video'; cat = '';
    if (chosen) hideDetail(); else paint();
  };
  search.oninput = () => { cat = ''; paint(); };

  /** a template's pages, its fonts loaded (a text measured with a stand-in font wraps differently) */
  async function pagesOf(it: Item): Promise<{ pages: Array<{ objects: string; size: PageSize }>; notes: string[] }> {
    if (it.src === 'builtin') {
      const { buildTemplate } = await import('../templates/build');
      const pics = await aiPicturesOf(it.t);
      const b = await buildTemplate(it.t, pics.pictures);
      return { pages: [{ objects: b.objects, size: { w: b.w, h: b.h } }], notes: [...pics.notes, ...b.notes] };
    }
    const r = await api.takeTemplate(it.t.id);
    const split = splitPages(r.json);
    const sizes = sizesOf(split, { w: r.width, h: r.height });
    const objs = split.pages.flatMap(p => JSON.parse(p) as Array<{ type?: string; fontFamily?: string; fontWeight?: number }>);
    const failed = await loadFonts(fontNeeds(objs.filter(o => o.type === 'thaiTextbox').map(o => ({ type: 'thaiTextbox', fontFamily: o.fontFamily ?? 'Sarabun', fontWeight: o.fontWeight ?? 400 }))));
    for (const o of objs) if (o.fontFamily) fabric.util.clearFabricFontCache(o.fontFamily);
    return {
      pages: split.pages.map((p, k) => ({ objects: p, size: sizes[k] })),
      notes: failed.length ? [`โหลดฟอนต์ ${failed.join(', ')} ไม่สำเร็จ ตัวอักษรอาจไม่เหมือนตัวอย่าง`] : []
    };
  }

  /** "แทนหน้านี้" / "เพิ่มเป็นหน้าใหม่": into the work that is open */
  async function putIn(it: Item, mode: 'replace' | 'add') {
    if (busy) return;
    say('');
    const no = hooks.cannotEdit() ?? hooks.busy();
    if (no) { say(`ใส่ลงงานนี้ไม่ได้: ${no} (เลือก "เปิดเป็นงานใหม่" ได้)`); return; }
    busy = true; paintButtons(); paint();
    try {
      const { pages, notes } = await pagesOf(it);
      // the page may have changed or started loading while the fonts came
      const late = hooks.cannotEdit();
      if (late) { say(`ใส่ลงงานนี้ไม่ได้: ${late}`); return; }
      const why = history.putPages(pages, mode);
      if (why) { say(`ใส่เทมเพลตไม่ได้: ${why}`); return; }
      hideDetail();
      if (!matchMedia('(min-width: 900px)').matches) closeSheets();
      toast([mode === 'replace' ? `ใช้ "${it.name}" แทนหน้านี้แล้ว (กดเลิกทำเพื่อย้อนกลับได้)` : `เพิ่ม "${it.name}" เป็นหน้าใหม่แล้ว`,
        hasSlots(pages) ? 'แตะช่องใส่รูป แล้วกด "ใส่รูป" เพื่อใส่รูปของคุณ' : '', ...notes].filter(Boolean).join(' · '), notes.length > 0);
      // เอกสาร: a template with ช่องข้อมูล opens its panel (src/ui/docFill.ts)
      if (hasFields(pages)) ed.canvas.fire('doc:fill' as never);
    } catch (e) {
      say('ใช้เทมเพลตไม่สำเร็จ: ' + errText(e));
      if (it.src === 'group') void load();
    } finally { busy = false; paintButtons(); paint(); }
  }
  const hasSlots = (pages: Array<{ objects: string }>) => pages.some(p => p.objects.includes('"picSlot"'));
  /** เอกสาร: pages with ช่องข้อมูล (src/docs/docFields.ts) */
  const hasFields = (pages: Array<{ objects: string }>) => pages.some(p => p.objects.includes('"field":{'));

  /**
   * the AI pictures of a ready-made template's slots, made the person's own first (/take: so a work shared with others
   * shows them too); anything that keeps a slot empty is said, never skipped silently
   */
  async function aiPicturesOf(t: BuiltinTemplate): Promise<{ pictures: Map<number, string>; notes: string[] }> {
    const pictures = new Map<number, string>();
    const st = ai[t.id];
    if (!st?.slots.length) return { pictures, notes: [] };
    if (saver.mode !== 'online') return { pictures, notes: [`ตอนนี้ออฟไลน์ (${saver.reason}) รูป AI ของเทมเพลตนี้จึงยังไม่ได้ใส่ ช่องยังว่าง ใส่รูปเองได้`] };
    try {
      const r = await api.aiImage.take(t.id);
      for (const s of r.slots) pictures.set(s.index, s.url);
      return { pictures, notes: r.missing ? [`รูป AI ${r.missing} ช่องของเทมเพลตนี้เปิดไม่ได้ (ไฟล์อาจหาย) ช่องนั้นยังว่าง`] : [] };
    } catch (e) {
      return { pictures, notes: [`ใส่รูป AI ของเทมเพลตนี้ไม่ได้: ${errText(e)} (ช่องยังว่าง ใส่รูปเองได้)`] };
    }
  }

  /* ---------- รูปประกอบด้วย AI: the admin part of a ready-made template's details (0022) ---------- */

  const sayAi = (text: string, bad = true) => { const m = $('tplAiMsg'); m.textContent = text; m.hidden = !text; m.classList.toggle('ok', !bad); };
  const aiSize = (sl: { w: number; h: number }) => Math.max(sl.w, sl.h) > (aiInfo?.twoKFromPx ?? 1400) ? '2K' as const : '1K' as const;
  const shownBuiltin = () => chosen?.src === 'builtin' ? chosen.t : null;

  /** the admin part: drawn again only when its state changes (the timer changes one button's words only) */
  function paintAi() {
    const box = $('tplAi'), t = shownBuiltin();
    box.hidden = !t || !hooks.isAdmin();
    if (box.hidden || !t) return;
    const slots = tplPhotoSlots(t), st = ai[t.id];
    const has = (i: number) => st?.slots.find(x => x.index === i) ?? null;
    const empty = slots.filter(sl => !has(sl.index));
    const off = aiError || (!aiInfo ? 'กำลังโหลดข้อมูลรูป AI…' : aiInfo.problem ?? (aiInfo.capUsd === 0 ? 'รูป AI ปิดอยู่ (เพดานต่อเดือน 0 ดอลลาร์) ตั้งเพดานได้ที่แผง "สร้างด้วย AI" ของช่องใส่รูป' : null));
    // why it cannot be used now (key, cap 0, loading) stays in this line, so it goes away by itself once fixed
    $('tplAiUse').textContent = !slots.length ? 'เทมเพลตนี้ไม่มีช่องใส่รูป'
      : off ? off
        : `${slots.length} ช่อง · มีรูป AI แล้ว ${slots.length - empty.length} ช่อง` + (aiInfo ? ` · เดือนนี้ใช้ไป $${usd(aiInfo.usedUsd)} จากเพดาน $${usd(aiInfo.capUsd)}` : '');
    $('tplAiUse').classList.toggle('bad', !!off);
    const all = $<HTMLButtonElement>('tplAiAll');
    const cost = empty.reduce((n, sl) => n + (aiInfo?.estimates[aiSize(sl)] ?? 0), 0);
    if (!aiBusy) all.textContent = `สร้างรูปทุกช่องที่ยังว่าง (${empty.length} ช่อง ราว $${usd(cost)})`;
    all.disabled = !!aiBusy || !!off;
    all.hidden = !empty.length && !aiBusy;   // every slot has one: made again one by one below
    const list = $('tplAiList');
    list.textContent = '';
    for (const sl of slots) {
      const h = has(sl.index);
      const li = document.createElement('li');
      li.className = 'tplairow';
      if (h?.url) li.append(Object.assign(document.createElement('img'), { src: h.url, alt: `รูป AI ช่องที่ ${sl.index + 1}` }));
      else li.append(Object.assign(document.createElement('span'), { className: 'aislot', textContent: 'ยังไม่มีรูป' }));
      li.append(Object.assign(document.createElement('small'), { textContent: `ช่อง ${sl.index + 1}: ${sl.hint} · รูป ${aiSize(sl)} ราว $${usd(aiInfo?.estimates[aiSize(sl)] ?? 0)}` }));
      const row = document.createElement('div');
      row.className = 'addrow';
      const mk = Object.assign(document.createElement('button'), { className: 'btn', textContent: h ? 'สร้างใหม่' : 'สร้าง' });
      mk.dataset.aiSlot = String(sl.index);
      mk.disabled = !!aiBusy || !!off;
      mk.onclick = () => void makeAi(t, [sl.index]);
      row.append(mk);
      if (h) {
        const rm = Object.assign(document.createElement('button'), { className: 'btn', textContent: 'เอาออก' });
        rm.disabled = !!aiBusy;
        rm.onclick = () => void removeAi(t, sl.index);
        row.append(rm);
      }
      li.append(row);
      list.append(li);
    }
    tickAi();
  }
  function tickAi() {
    if (!aiBusy) return;
    const secs = Math.round((Date.now() - aiBusy.started) / 1000);
    const b = document.querySelector<HTMLButtonElement>(`#tplAiList [data-ai-slot="${aiBusy.slot}"]`);
    if (b) b.textContent = `กำลังสร้าง… ${secs} วิ`;
    $('tplAiAll').textContent = `กำลังสร้างช่องที่ ${aiBusy.slot + 1}… ${secs} วิ`;
  }

  /** the AI pictures of every ready-made template (+ the prices for admins) */
  async function loadAi() {
    try { ai = (await api.aiImage.templates()).templates; aiError = ''; }
    catch (e) { ai = {}; aiError = 'โหลดรูป AI ของเทมเพลตสำเร็จรูปไม่สำเร็จ: ' + errText(e) + ' (ใช้เทมเพลตได้ ช่องจะว่าง)'; }
  }

  /** the details of a ready-made template opened by an admin: prices, and its preview made again if it is out of date */
  async function openAi(t: BuiltinTemplate) {
    try { aiInfo = await api.aiImage.info(); } catch (e) { aiInfo = null; aiError = 'โหลดข้อมูลรูป AI ไม่สำเร็จ: ' + errText(e); }
    if (shownBuiltin() !== t) return;
    paintAi();
    const st = ai[t.id];
    if (st?.slots.length && !st.thumb && !aiBusy) await makeThumb(t);
  }

  /** "สร้างรูปทุกช่องที่ยังว่าง": one after another (a failing one stops the rest) */
  $('tplAiAll').onclick = () => {
    const t = shownBuiltin();
    if (!t || aiBusy) return;
    const st = ai[t.id];
    const empty = tplPhotoSlots(t).filter(sl => !st?.slots.some(x => x.index === sl.index)).map(sl => sl.index);
    if (empty.length) void makeAi(t, empty);
  };

  async function makeAi(t: BuiltinTemplate, slots: number[]) {
    if (aiBusy) return;
    sayAi('');
    aiStop = false;
    let made = 0, spent = 0;
    window.clearInterval(aiTimer);
    aiTimer = window.setInterval(tickAi, 1000);
    try {
      for (const i of slots) {
        if (aiStop) { toast(`หยุดสร้างรูป AI ของ "${t.th}" แล้ว (สร้างเสร็จ ${made} ช่อง)`, true); break; }
        aiBusy = { tpl: t.id, slot: i, started: Date.now() };
        if (shownBuiltin() === t) paintAi();
        try {
          const r = await api.aiImage.makeForTemplate(t.id, i);
          made++; spent += r.image.costUsd;
          if (aiInfo) { aiInfo.usedUsd = r.usedUsd; aiInfo.capUsd = r.capUsd; }
        } catch (e) {
          // a failing slot stops the rest: the next ones would most likely fail the same way (and may be charged)
          const why = `สร้างรูปช่องที่ ${i + 1} ไม่สำเร็จ: ${errText(e)}` + (slots.length > 1 ? ' · จึงหยุดช่องที่เหลือไว้ก่อน' : '');
          if (shownBuiltin() === t) sayAi(why); else toast(why, true);
          break;
        }
      }
    } finally {
      aiBusy = null; window.clearInterval(aiTimer);
    }
    await loadAi();
    paint();
    if (shownBuiltin() === t) {
      paintAi();
      if (made && !$('tplAiMsg').textContent) sayAi(`สร้างรูปแล้ว ${made} ช่อง (คิดเงินรวม $${usd(spent)}) · ขึ้นให้ทุกคนแล้ว`, false);
    }
    if (made) await makeThumb(t);
  }

  async function removeAi(t: BuiltinTemplate, slot: number) {
    if (aiBusy) return;
    sayAi('');
    try {
      const r = await api.aiImage.removeFromTemplate(t.id, slot);
      if (r.template.slots.length) ai[t.id] = r.template; else delete ai[t.id];
      sayAi(`เอารูป AI ออกจากช่องที่ ${slot + 1} แล้ว (งานที่ใช้ไปแล้วยังมีรูปเดิม)`, false);
    } catch (e) { sayAi('เอารูปออกไม่สำเร็จ: ' + errText(e)); await loadAi(); }
    paint(); paintAi();
    if (ai[t.id]?.slots.length && !ai[t.id].thumb) await makeThumb(t);
  }

  /**
   * The card's preview drawn with the AI pictures now in the slots, stored with which pictures it was drawn from (the
   * server keeps it only while they are still the same — another device may have changed a slot meanwhile).
   */
  async function makeThumb(t: BuiltinTemplate) {
    const st = ai[t.id];
    if (!st?.slots.length) return;
    try {
      const pics = new Map(st.slots.filter(x => x.url).map(x => [x.index, x.url!] as const));
      const { buildTemplate } = await import('../templates/build');
      const b = await buildTemplate(t, pics);
      // a preview missing a picture or drawn with a stand-in font would mislead everyone: not stored, said
      if (b.notes.length) throw new Error(b.notes.join(' · '));
      const { renderThumbnail } = await import('../export/render');
      const url = await renderThumbnail(JSON.stringify({ objects: JSON.parse(b.objects) }), { w: t.w, h: t.h, name: t.th, bg: t.bg }, 360);
      const jpeg = await (await fetch(url)).blob();
      const r = await api.aiImage.putThumb(t.id, st.madeFrom, jpeg);
      if (ai[t.id]?.madeFrom === st.madeFrom) ai[t.id] = { ...ai[t.id], thumb: r.thumb };
      paint();
      if (shownBuiltin() === t) poster.src = thumbOf(t);
    } catch (e) {
      const stale = e instanceof ApiError && e.body.code === 'thumb_stale';
      const why = `ทำรูปตัวอย่างแบบมีรูป AI ไม่สำเร็จ: ${errText(e)} (ในแผงยังใช้รูปตัวอย่างเดิม${stale ? ' · เปิดเทมเพลตนี้อีกครั้งแล้วระบบจะทำให้ใหม่' : ''})`;
      if (shownBuiltin() === t) sayAi(why); else toast(why, true);
      if (stale) { await loadAi(); paint(); paintAi(); }
    }
  }

  /** "เปิดเป็นงานใหม่" (and a video template's "ใช้เทมเพลตนี้") */
  async function openAsNew(it: Item) {
    if (busy) return;
    say('');
    const other = hooks.busy();
    if (other) { say(`ยังเปิดเทมเพลตไม่ได้: ${other}`); return; }
    const why = await saver.flush();
    if (why) { say(`ยังเปิดเทมเพลตไม่ได้ เพราะ${why} เพื่อไม่ให้การแก้ไขหาย`); return; }
    busy = true; paintButtons(); paint();
    try {
      if (it.src === 'builtin') {
        const { pages, notes } = await pagesOf(it);
        await saver.newDesign();
        ed.doc.name = it.name;
        const no = history.putPages(pages, 'replace');
        if (no) { say(`เปิดเป็นงานใหม่ไม่ได้: ${no}`); return; }
        history.reset();      // the new work starts as the template (เลิกทำ does not go back to an empty page)
        saver.changed();
        hideDetail(); closeSheets();
        toast([`เริ่มงานใหม่ "${it.name}" จากเทมเพลตแล้ว`, hasSlots(pages) ? 'แตะช่องใส่รูป แล้วกด "ใส่รูป"' : '', ...notes].filter(Boolean).join(' · '), notes.length > 0);
        if (hasFields(pages)) ed.canvas.fire('doc:fill' as never);
        return;
      }
      const t = it.t;
      const { design } = await api.useTemplate(t.id);
      const res = await api.get(design.id);
      await saver.openDesign(res.design, res.json);
      hideDetail();
      closeSheets();
      toast(kindOf(t) === 'video'
        ? `สร้างวิดีโอ "${design.name}" จากเทมเพลตแล้ว เลือกคลิปหรือรูป ${t.slots ?? ''} ไฟล์ใส่ช่องว่างได้เลย`
        : `สร้างงานใหม่ "${design.name}" จากเทมเพลตแล้ว เป็นงานของคุณ แก้ได้เลย`);
      hooks.onUsed({ ...design, kind: design.kind ?? kindOf(t) });
    } catch (e) {
      say('ใช้เทมเพลตไม่สำเร็จ: ' + errText(e));
      if (it.src === 'group') void load();
    } finally { busy = false; paintButtons(); paint(); }
  }

  /** the group's templates (the ready-made ones are always there, even offline) */
  async function load() {
    if (saver.mode !== 'online') {
      list = []; cats = [];
      loadError = `เทมเพลตของกลุ่มยังโหลดไม่ได้: ${saver.reason} (เทมเพลตสำเร็จรูปใช้ได้)`;
      say(loadError); paint();
      return;
    }
    try {
      const [r] = await Promise.all([api.templates(), loadAi()]);
      cats = r.categories; list = r.templates; loadError = '';
      if (cat.startsWith('g:') && !list.some(t => 'g:' + t.categoryId === cat && kindOf(t) === kind)) cat = '';
      if (chosen?.src === 'group' && !list.some(t => t.id === chosen!.id)) hideDetail();
      say(aiError); paint(); paintAi();
    } catch (e) { loadError = 'โหลดเทมเพลตของกลุ่มไม่สำเร็จ: ' + errText(e) + ' (เทมเพลตสำเร็จรูปใช้ได้)'; say([loadError, aiError].filter(Boolean).join(' · ')); paint(); }
  }

  return {
    load,
    /** "+ วิดีโอจากเทมเพลต" (งานของฉัน): the video templates */
    openVideo() { kind = 'video'; cat = ''; if (chosen) hideDetail(); openSheet('shTemplates'); }
  };
}
